import { FC, ReactNode, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import {
  MplusStatuses,
  MplusStatusesTypes,
  TypePayment,
} from 'widgets/mplus/mplus-payment/module'

import { CalculatedMplusInfo, ICreditInfo } from 'entities/mplus/module'
import { mplusStore } from 'entities/mplus/module/mplus-store'
import userStore from 'entities/user/module/user-store'

import { CreditInfo } from 'shared/api/mplus/credit-info'
import { MIN_MPLUS_PRICE, isShowingMplus } from 'shared/constants'
// import { useGetUserIsDeveloper } from 'shared/lib/userIsDeveloper'
import { Paper } from 'shared/ui/paper'

import { getCurrentStatusMplus } from '../lib/getCurrentStatusMplus'

import { MplusActive } from './components/mplus-active'
import { MplusAllowed } from './components/mplus-allowed'
import { MplusLoading } from './components/mplus-loading'
import { MplusNotAllow } from './components/mplus-not-allow'
import { MplusNotAllowed } from './components/mplus-not-allowed'
import { MplusPriceLess } from './components/mplus-price-less'
import s from './styles.module.scss'

interface Props {
  price?: string
  slot?: ReactNode
  children?: ReactNode
  tranchePrice?: string
  countPassengers?: number
  confirmComponent?: ReactNode
  isShowMplusPayment?: boolean
  onPayRegular?: () => void
  onPayMplus?: (dayInMonth: number) => void
  onOpenMplus?: () => void
}

const MplusUnActiveList = [
  MplusStatuses.INCLUDE.key,
  MplusStatuses.NOT_ALLOW_PRICE.key,
  MplusStatuses.NOT_ALLOW.key,
] as string[]

const MplusActiveList = [
  MplusStatuses.NOT_ENOUGH_CASH.key,
  MplusStatuses.ALLOW.key,
] as string[]

export const MplusPayment: FC<Props> = observer(
  ({
    slot,
    price,
    children,
    tranchePrice,
    countPassengers,
    confirmComponent,
    isShowMplusPayment = true,
    onPayMplus,
    onOpenMplus,
  }) => {
    const { t } = useTranslation()

    const creditMplusStore = mplusStore.getMplusInfo()

    const userInfoStore = userStore.getAccountInfo()

    const userSettings = userStore.getUserSettings()

    const [calculatedInfo, setCalculatedInfo] = useState<CalculatedMplusInfo>()

    const [typePayment, setTypePayment] = useState<TypePayment>(
      TypePayment.Regular,
    )

    const [mplusStatus, setMplusStatus] = useState<MplusStatusesTypes>(
      MplusStatuses.INCLUDE.key,
    )

    const [isLoading, setIsLoading] = useState<boolean>(true)

    const getCalculatedInfo = async (userMplusState: ICreditInfo) => {
      const mplusStatusPay = getCurrentStatusMplus({
        priceRequest: parseInt(tranchePrice || '0'),
        mplusStatus: userMplusState?.status as string,
        userRole: userInfoStore?.mbank_user_role as string,
        availableAmountMplus: parseInt(userMplusState?.available_amount || '0'),
      })

      setMplusStatus(mplusStatusPay)

      if (
        !tranchePrice ||
        !countPassengers ||
        mplusStatusPay !== MplusStatuses.ALLOW.key
      ) {
        setIsLoading(false)
        return
      }

      try {
        const { data: response } = await CreditInfo.getCalculatedInfo({
          price: Number(price),
          quantity: countPassengers,
        })

        setCalculatedInfo(response?.data)
      } catch {
      } finally {
        setIsLoading(false)
      }
    }

    const getCreditInfo = async () => {
      if (!userInfoStore?.is_mplus_available) {
        setIsLoading(false)

        setMplusStatus(MplusStatuses.NOT_ALLOW_MPLUS.key)

        return
      }

      if (!creditMplusStore) {
        try {
          const { data: response } = await CreditInfo.getCreditInfo()

          getCalculatedInfo(response?.data)

          mplusStore.setCreditInfo(response?.data)
        } catch (e) {
          if (parseInt(price || '0') < MIN_MPLUS_PRICE) {
            setMplusStatus(MplusStatuses.NOT_ALLOW_PRICE.key)
          }

          setIsLoading(false)
        }
      } else {
        getCalculatedInfo(creditMplusStore)
      }
    }

    useEffect(() => {
      // const isDevUser = useGetUserIsDeveloper()
      if (!userSettings?.is_mplus_available) {
        setMplusStatus(MplusStatuses.NOT_ALLOW.key)
        setIsLoading(false)

        return
      }

      if (!isShowingMplus) {
        setMplusStatus(MplusStatuses.NOT_ALLOW.key)
        setIsLoading(false)

        return
      }

      if (!isShowMplusPayment) {
        setMplusStatus(MplusStatuses.NOT_ALLOW.key)
        setIsLoading(false)

        return
      }

      // if (!isDevUser) {
      //   setMplusStatus(MplusStatuses.NOT_ALLOW.key)
      //   setIsLoading(false)

      //   return
      // }

      getCreditInfo()
    }, [])

    return (
      <Paper padding={[20, 16]}>
        <div className={s.container}>
          {MplusStatuses.NOT_ALLOW.key !== mplusStatus && (
            <p className={clsx(s.title, 'semi-bold-16')}>
              {t('mplus.payment_type')}
            </p>
          )}

          {isLoading && (
            <div className={s.typePayment}>
              <MplusLoading />
            </div>
          )}

          {!isLoading && mplusStatus !== MplusStatuses.NOT_ALLOW.key && (
            <div className={s.typePayment}>
              {mplusStatus === MplusStatuses.INCLUDE.key && (
                <MplusNotAllowed onOpenMplus={onOpenMplus} />
              )}

              {mplusStatus === MplusStatuses.NOT_ALLOW_MPLUS.key && (
                <MplusNotAllow />
              )}

              {mplusStatus === MplusStatuses.NOT_ALLOW_PRICE.key && (
                <MplusPriceLess />
              )}

              {(mplusStatus === MplusStatuses.NOT_ENOUGH_CASH.key ||
                mplusStatus === MplusStatuses.ALLOW.key) && (
                <MplusAllowed
                  typePayment={typePayment}
                  onChangeTypePayment={(type) => setTypePayment(type)}
                />
              )}
            </div>
          )}
          {typePayment === TypePayment.Mplus &&
            MplusActiveList?.includes(mplusStatus) && (
              <MplusActive
                onSubmit={onPayMplus}
                mplusStatus={mplusStatus}
                confirmComponent={confirmComponent}
                limitMplus={creditMplusStore?.available_amount}
                mplusCreditInfo={{
                  countPassengers: countPassengers,
                  creditMoneyEachMonth: calculatedInfo?.monthly_amount,
                  eachPassengerPayInfo:
                    calculatedInfo?.monthly_amount_per_passenger,
                }}
              />
            )}

          {typePayment === TypePayment.Regular && slot && (
            <div className={s.slot}>{slot}</div>
          )}

          {(typePayment === TypePayment.Regular ||
            MplusUnActiveList?.includes(mplusStatus)) &&
            children}
        </div>
      </Paper>
    )
  },
)
